/**
 * Auth helpers for the portal SPA.
 *
 * Wraps `supabase-js` magic-link flow with role-aware redirect logic, session
 * shape extraction, and the operator 7-day session-window enforcement
 * documented in ARCH-002 §7.
 *
 * Magic-link only per ARCH-002 §2. Sending the link goes through Resend custom
 * SMTP per ARCH-010 (Proposed) once Eric has the SMTP credentials in Supabase
 * project settings; supabase-js does not need to know about the SMTP wiring.
 */

import type { Session } from '@supabase/supabase-js';
import { supabase } from './supabase.js';

/**
 * Role claim shape inside `auth.users.app_metadata`. Set server-side only via a
 * privileged migration (T-016). Subscribers either have `role: 'subscriber'`
 * or no role key at all; both are treated as subscriber.
 */
export type AuthRole = 'operator' | 'subscriber';

/**
 * Operator session window in milliseconds. ARCH-002 §7 mandates 7 days for
 * operator accounts versus 30 days for subscribers. Supabase Auth itself only
 * supports one refresh-token TTL, so the operator shortening is enforced here
 * by checking `session.user.last_sign_in_at` against this window on every
 * authenticated request and forcing re-auth past it.
 */
export const OPERATOR_SESSION_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Extract the role claim from a Supabase session. Defaults to 'subscriber' if
 * the claim is missing or unrecognized.
 */
export function getRoleFromSession(session: Session | null): AuthRole {
  if (!session) return 'subscriber';
  const role = session.user?.app_metadata?.role;
  return role === 'operator' ? 'operator' : 'subscriber';
}

/**
 * Returns true if the session is a stale operator session past the 7-day
 * window. Used by route guards to force re-auth on the operator surface.
 */
export function isOperatorSessionExpired(session: Session | null): boolean {
  if (!session) return false;
  if (getRoleFromSession(session) !== 'operator') return false;
  const lastSignIn = session.user?.last_sign_in_at;
  if (!lastSignIn) return true;
  const lastSignInMs = new Date(lastSignIn).getTime();
  if (Number.isNaN(lastSignInMs)) return true;
  return Date.now() - lastSignInMs > OPERATOR_SESSION_WINDOW_MS;
}

/**
 * Resolve the post-sign-in landing path for a session. Operators go to
 * `/eric`; everyone else lands on `/portal`.
 */
export function getPostSignInPath(session: Session | null): '/portal' | '/eric' {
  return getRoleFromSession(session) === 'operator' ? '/eric' : '/portal';
}

/**
 * Send a magic link to `email`. The redirect URL is derived from
 * `window.location.origin` so local dev hits 127.0.0.1:5173 and production
 * hits app.yesplease.studio. Both are whitelisted in supabase/config.toml.
 *
 * Returns the supabase-js result; the caller decides how to surface success.
 */
export async function sendMagicLink(email: string) {
  const redirectTo = `${window.location.origin}/auth/callback`;
  return supabase.auth.signInWithOtp({
    email,
    options: {
      // shouldCreateUser must be true for first-time subscribers reaching the
      // app via the post-Stripe-Checkout flow (their `auth.users` row is
      // created by the checkout webhook handler, T-031, but until that ships
      // the magic link is also the account-creation path).
      shouldCreateUser: true,
      emailRedirectTo: redirectTo,
    },
  });
}

/**
 * Sign out the current session per ARCH-002. The post-sign-out redirect is
 * performed by the caller (`/auth/sign-out` routes to the marketing site,
 * `https://yesplease.studio`); this helper only clears the supabase-js
 * session.
 */
export async function signOut() {
  return supabase.auth.signOut();
}

/**
 * Read the current session synchronously from supabase-js memory. Used by
 * route guards. Returns null if no session is loaded yet.
 */
export async function getCurrentSession(): Promise<Session | null> {
  const { data } = await supabase.auth.getSession();
  return data.session ?? null;
}
